import React, { useState, useEffect } from 'react';

import { dataGridPremiumColumns } from '../../utils/helper';
import { DataGridIdentifiers, DatagridPremiumUi, DialogUI } from '../Interface';
import sourcelistService from '../../service/sourcelist.service';
import { Button } from '@mui/material';
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from '@mui/x-data-grid-premium';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import dayjs from 'dayjs';
import SourceListImportDialog from './SourceListImportDialog';
import prodgeneratorService from '../../service/prodgenerator.service';
import { isEqual } from 'lodash';
import useGenerateJokerReportCols from '../../hooks/useGenerateJokerReportCols';

const CustomToolbar = ({ handleUploadClick }) => {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <Button
        size="small"
        sx={{ ml: 'auto' }}
        startIcon={<FileUploadOutlinedIcon />}
        onClick={() => {
          handleUploadClick();
        }}
      >
        Import
      </Button>
    </GridToolbarContainer>
  );
};

const SourceListReports = ({
  refetch,
  values,
  viewField,
  operatorsDisabled,
  rowSelection,
  autocompleteOptions,
  selectedItemCode,
  jokerFields,
}) => {
  const { refetchTable, setRefetchTable } = refetch;
  const { setSelectedValues } = values;
  const { rowSelectionModel, setRowSelectionModel } = rowSelection;
  //const { setFill } = fillState;

  const [reports, setReports] = useState({ rows: [], rowCount: 0 });
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [filterItemCodes, setFilterItemCodes] = useState([]);
  const [firstLoad, setFirstLoad] = useState(true);

  const [initialState, setInitialState] = useState({
    filter: {
      filterModel: {
        items: [],
        logicOperator: 'and',
      },
    },
    sorting: {
      sortModel: [{ field: 'itemCode', sort: 'asc' }],
    },
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
    density: 'compact',
  });

  useEffect(() => {
    if (operatorsDisabled) {
      setRowSelectionModel([]);
    }
  }, [operatorsDisabled]);

  useEffect(() => {
    if (selectedItemCode) {
      getSourceListItems();
    }
  }, [selectedItemCode]);

  useEffect(() => {
    if (selectedItemCode && reports.rows.length > 0 && firstLoad) {
      setSelectedValues(reports.rows[0]);
      setRowSelectionModel([reports.rows[0].itemCode]);
      setFirstLoad(false);
    }
  }, [selectedItemCode, reports.rows, firstLoad]);

  const getSourceListItems = async () => {
    const data = await prodgeneratorService.getsourcelistByItemCode({ itemCode: selectedItemCode });
    /*if (data.sourceData.length === 1) {
          setFill(true);
        }*/
    const fItemCodes = data.sourceData.map((item) => item.relatedItemCode);
    setFilterItemCodes(fItemCodes);

    const initState = {
      filter: {
        filterModel: {
          items: fItemCodes.map((itemCode) => ({
            field: 'itemCode',
            operator: 'equals',
            value: itemCode,
          })),

          logicOperator: 'or',
        },
      },
    };
    if (isEqual(initState, initialState)) return initialState;
    setInitialState(initState);
    return initState;
  };

  const getList = async (gridState, signal) => {
    if (selectedItemCode) {
      const query = await getSourceListItems();
      return sourcelistService.getSourcelists(query).then((data) => {
        setReports(data);
      });
    } else {
      sourcelistService.getSourcelists(gridState, signal).then((data) => {
        setReports(data);
      });
    }
  };

  const jokerCols = useGenerateJokerReportCols(jokerFields);

  const columns = dataGridPremiumColumns([
    {
      field: 'itemCode',
      type: 'string',
      headerName: 'Cikkszám',
    },
    {
      field: 'itemName',
      type: 'string',
      headerName: 'Cikknév',
      sortable: false,
      renderCell: (data) => {
        return data.row.item?.itemName;
      },
    },
    {
      field: 'baseQuantity',
      type: 'number',
      headerName: 'Mennyiség',
    },
    {
      field: 'quantityUnitId',
      type: 'string',
      headerName: 'Me.',
      filterable: false,
      sortable: false,
      renderCell: (data) => {
        return data.row.item.quantityUnit?.code;
      },
    },
    {
      field: 'createdBy',
      type: 'singleSelect',
      headerName: 'Létrehozta',
      valueOptions: autocompleteOptions.user,
      renderCell: (data) => {
        return autocompleteOptions.user.find((u) => u.value === data.row.createdBy)?.label ?? '';
      },
    },
    {
      field: 'createdAt',
      type: 'date',
      headerName: 'Létrehozás dátuma',
      valueGetter: (_data, row) => {
        return new Date(row.createdAt);
      },
      renderCell: (data) => {
        return dayjs(data.row.createdAt).format('YYYY.MM.DD HH:mm');
      },
    },
    ...jokerCols,
  ]);

  const handleUploadClick = () => {
    setUploadDialogOpen(true);
  };

  return (
    <>
      <DatagridPremiumUi
        datagridIdentifier={DataGridIdentifiers.sourceList}
        datagridInitialState={initialState}
        filterFromInitialState={filterItemCodes.length > 0}
        dataLoader={getList}
        rows={reports.rows}
        refetch={{ refetchTable, setRefetchTable }}
        rowCount={reports.rowCount}
        columns={[...columns]}
        onRowClick={(rowData) => {
          if (!viewField) return;
          setSelectedValues(rowData.row);
        }}
        getRowId={(row) => row.itemCode}
        paginationAndfilterMode={filterItemCodes.length > 0 ? 'client' : 'server'}
        onRowSelectionModelChange={(newRowSelectionModel) => {
          setRowSelectionModel(newRowSelectionModel);
        }}
        slots={{
          toolbar: CustomToolbar,
        }}
        slotProps={{
          toolbar: {
            handleUploadClick,
          },
        }}
        rowSelectionModel={rowSelectionModel}
        disableRowSelectionOnClick={!viewField}
      />

      <DialogUI
        open={uploadDialogOpen}
        headerContent={'Importálás'}
        fullWidth={true}
        onClose={() => setUploadDialogOpen(false)}
        maxWidth="md"
      >
        <SourceListImportDialog
          autocompleteOptions={autocompleteOptions}
          onClose={() => setUploadDialogOpen(false)}
          refetch={refetch}
        />
      </DialogUI>
    </>
  );
};

export default SourceListReports;
